import { CustomMultiSelectFilter, FilterOption, ToggleFilter } from '@britishcouncil/react-admin';
import { CentreTypeSelector } from 'api';

export type LocationFilterDefinitions = {
  regionsIds: CustomMultiSelectFilter<number>;
  countriesIds: CustomMultiSelectFilter<number>;
  centresIds: CustomMultiSelectFilter<string>;
  centreType: ToggleFilter<number>;
};

type LocationFilterKey = 'regionsIds' | 'countriesIds' | 'centresIds' | 'centreType';

type GetLocationFilterDefinitionsParams = {
  regionsOptions: FilterOption<number>[];
  countriesOptions: FilterOption<number>[];
  centresOptions: FilterOption<string>[];
  centreType?: CentreTypeSelector;
  availableFilters?: LocationFilterKey[];
};

const DEFAULT_AVAILABLE_FILTERS: LocationFilterKey[] = [
  'regionsIds',
  'countriesIds',
  'centresIds',
  'centreType',
];

// Overload 1: When availableFilters is NOT provided - return full type
export function getLocationFilterDefinitions(
  params: Omit<GetLocationFilterDefinitionsParams, 'availableFilters'>
): LocationFilterDefinitions;

// Overload 2: When availableFilters IS provided - return partial type
export function getLocationFilterDefinitions(
  params: GetLocationFilterDefinitionsParams & { availableFilters: LocationFilterKey[] }
): Partial<LocationFilterDefinitions>;

// Implementation
export function getLocationFilterDefinitions({
  regionsOptions,
  countriesOptions,
  centresOptions,
  centreType,
  availableFilters = DEFAULT_AVAILABLE_FILTERS,
}: GetLocationFilterDefinitionsParams):
  | LocationFilterDefinitions
  | Partial<LocationFilterDefinitions> {
  const result: Partial<LocationFilterDefinitions> = {};

  if (availableFilters.includes('regionsIds')) {
    result.regionsIds = {
      type: 'custom-multiselect',
      label: 'Region',
      options: regionsOptions,
      dropdownOptions: {
        placeholder: 'Search...',
        search: true,
        selectedOnTop: true,
        isClearable: true,
        style: { maxWidth: '250px', width: '250px' },
        disabled: centreType !== CentreTypeSelector.BC,
      },
    };
  }

  if (availableFilters.includes('countriesIds')) {
    result.countriesIds = {
      type: 'custom-multiselect',
      label: 'Countries / Territories',
      options: countriesOptions,
      dropdownOptions: {
        placeholder: 'Search...',
        search: true,
        selectedOnTop: true,
        isClearable: true,
        style: { maxWidth: '250px', width: '250px' },
        disabled: centreType !== CentreTypeSelector.BC,
      },
    };
  }

  if (availableFilters.includes('centresIds')) {
    result.centresIds = {
      type: 'custom-multiselect',
      label: 'Centres',
      options: centresOptions,
      dropdownOptions: {
        placeholder: 'Search...',
        search: true,
        selectedOnTop: true,
        isClearable: true,
        style: { maxWidth: '250px', width: '250px' },
      },
    };
  }

  if (availableFilters.includes('centreType')) {
    result.centreType = {
      type: 'toggle',
      label: 'Type',
      unselectable: true,
      options: [
        { text: 'BC', value: CentreTypeSelector.BC },
        { text: 'ALL', value: CentreTypeSelector.ALL },
        { text: 'IDP', value: CentreTypeSelector.IDP },
      ],
    };
  }

  return result;
}
